// Создание и правка виджета: чтение формы, добавление на холст, сброс формы.

import type { VisualizationType, WidgetDefinition } from '../types';
import { t } from '../i18n';
import { indicatorNames, territoryNames } from '../data-locale';
import { dataTranslation } from '../ui-i18n';

import { DEFAULT_APPEARANCE, DEFAULT_TIME, createClientId } from './model';
import { addWidgetToGrid, findWidget, renderWidget, replaceWidgetInGrid } from './widgets';
import { clearFormError, errorMessage, markDirty, setStatus, showFormError, updateWidgetCounter } from './status';
import { controls } from './controls';
import { currentIndicator, indicatorSectionId, loadIndicatorSections, populateIndicators, sourceFilterForIndicator, updateAdvisor } from './indicators';
import { currentLocale, setIconButton } from './locale';
import { defaultWidgetLayout, readAppearanceControls, writeAppearanceControls } from './widget-appearance';
import { ensurePopulationPyramidDimensions, ensureSingleDimensionsForEmissMap, isMapVisualization, renderDimensions, updateClassificationControls, updateConditionControls, updateIndicatorControls, updateVisualizationSpecificControls, validateDimensionSelections } from './widget-form';
import { focusWidgetOnCanvas, setBuilderOpen, setPaletteOpen, updateInspectorState } from './workspace';
import { narrowWorkspaceMedia, scheduleGridMetricsUpdate, syncLayoutsFromGrid } from './canvas';
import { selectedValues } from './choice-lists';
import { state } from './state';
import { validateEmissWidgetPlan } from './emiss';

export function readWidgetFromForm(forceNew = false): WidgetDefinition {
  const indicator = currentIndicator();
  if (!indicator) throw new Error('Выберите показатель.');
  const territoryIds = selectedValues(controls.territories);
  if (!territoryIds.length) throw new Error('Выберите хотя бы одну территорию.');
  const years = selectedValues(controls.years).map(Number).filter(Number.isFinite);
  if (!years.length) throw new Error('Выберите хотя бы один год.');
  const months = indicator.frequency === 'monthly'
    ? selectedValues(controls.months).map(Number).filter(Number.isFinite)
    : [];
  const visualization = controls.visualization.value as VisualizationType;
  if (visualization === 'population_pyramid') ensurePopulationPyramidDimensions();
  ensureSingleDimensionsForEmissMap(visualization);
  const dimensions = validateDimensionSelections(visualization);
  if (isMapVisualization(visualization)
      && state.dimensions.length
      && Object.values(dimensions).some((values) => values.length !== 1)) {
    throw new Error('Для карты выберите по одному значению каждого аналитического разреза, чтобы каждой территории соответствовало одно значение.');
  }
  const existing = forceNew ? undefined : state.definition.widgets.find((widget) => widget.id === state.editingWidgetId);
  // Заголовок хранится на двух языках: title — русский, titleEn — английский.
  // Пустое поле — автозаголовок из названий показателя и территорий на своём
  // языке; свой русский заголовок без английского — перевод по словарю.
  const selectedTerritories = territoryIds
    .map((id) => state.territories.find((territory) => territory.id === id))
    .filter((territory) => territory != null)
    .map((territory) => territoryNames(territory));
  const names = indicatorNames(indicator);
  const single = selectedTerritories.length === 1 ? selectedTerritories[0] : undefined;
  const automaticTitle = single
    ? `${names.ru}: ${single.ru}`
    : `${names.ru}: ${selectedTerritories.length} территорий`;
  const automaticTitleEn = single
    ? `${names.en}: ${single.en}`
    : `${names.en}: ${selectedTerritories.length} territories`;
  const customTitle = controls.title.value.trim();
  const title = customTitle || automaticTitle;
  const titleEn = controls.titleEn.value.trim()
    || (customTitle ? dataTranslation(customTitle) ?? '' : automaticTitleEn);
  const appearance = readAppearanceControls();

  return {
    id: existing?.id ?? createClientId(),
    title,
    ...(titleEn ? { titleEn } : {}),
    visualization,
    query: {
      indicatorId: indicator.id,
      compareIndicatorIds: existing?.query.compareIndicatorIds ?? [],
      territoryIds,
      years,
      months,
      dimensions,
    },
    appearance,
    time: {
      activePeriod: existing?.time.activePeriod ?? null,
      showControl: controls.timeControl.checked,
    },
    layout: existing?.layout ?? defaultWidgetLayout(visualization),
  };
}

export async function editWidget(id: string): Promise<void> {
  const widget = findWidget(id);
  if (!widget) return;
  state.editingWidgetId = id;
  state.inspectorVisualization = widget.visualization;
  updateInspectorState();
  setBuilderOpen(true);
  state.suppressInspectorEvents = true;
  try {
    const indicator = state.indicators.find((item) => item.id === widget.query.indicatorId);
    controls.source.value = sourceFilterForIndicator(indicator);
    controls.indicatorSearch.value = '';
    await loadIndicatorSections();
    const widgetSectionId = indicator ? indicatorSectionId(indicator) : '';
    controls.indicatorSection.value = state.indicatorSections.some((section) => section.id === widgetSectionId)
      ? widgetSectionId
      : '';
    populateIndicators();
    controls.indicator.value = widget.query.indicatorId;
    await updateIndicatorControls(
      widget.visualization,
      widget.query.years,
      widget.query.months,
      widget.query.dimensions,
      widget.query.territoryIds,
    );
    controls.title.value = widget.title;
    controls.titleEn.value = widget.titleEn ?? '';
    controls.palette.value = widget.appearance.palette;
    controls.decimals.value = String(widget.appearance.decimals);
    controls.orientation.value = widget.appearance.orientation;
    controls.sortOrder.value = widget.appearance.sortOrder;
  controls.topN.value = String(widget.appearance.topN);
  controls.axisScale.value = widget.appearance.axisScale;
  controls.includeZero.value = widget.appearance.includeZero;
  controls.xAxisMin.value = widget.appearance.xAxisMin == null ? '' : String(widget.appearance.xAxisMin);
  controls.xAxisMax.value = widget.appearance.xAxisMax == null ? '' : String(widget.appearance.xAxisMax);
  controls.dataZoom.value = widget.appearance.dataZoom;
    controls.showPoints.value = widget.appearance.showPoints;
    controls.legendPosition.value = widget.appearance.legendPosition;
    controls.smooth.checked = widget.appearance.smoothLines;
    controls.patterns.checked = widget.appearance.usePatterns;
    controls.legend.checked = widget.appearance.showLegend;
    controls.labels.checked = widget.appearance.showLabels;
    controls.reverse.checked = widget.appearance.reversePalette;
    controls.classification.value = widget.appearance.classification;
    controls.classCount.value = String(widget.appearance.classCount);
    controls.manualBreaks.value = widget.appearance.manualBreaks.join('; ');
    controls.nullColor.value = widget.appearance.nullColor;
    controls.mapScaleMode.value = widget.appearance.mapScaleMode;
    controls.mapBasemap.value = widget.appearance.mapBasemap;
    controls.mapFixedMin.value = widget.appearance.mapFixedMin == null ? '' : String(widget.appearance.mapFixedMin);
    controls.mapFixedMax.value = widget.appearance.mapFixedMax == null ? '' : String(widget.appearance.mapFixedMax);
    controls.timeControl.checked = widget.time.showControl;
    controls.conditionOperator.value = widget.appearance.condition.operator;
    controls.conditionValue.value = widget.appearance.condition.value == null ? '' : String(widget.appearance.condition.value);
    controls.conditionColor.value = widget.appearance.condition.color;
    writeAppearanceControls(widget.appearance, widget.title, widget.time);
    updateClassificationControls();
    updateConditionControls();
    controls.visualization.value = widget.visualization;
    updateVisualizationSpecificControls();
    clearFormError();
  } finally {
    state.suppressInspectorEvents = false;
    updateInspectorState();
  }
  controls.builderPanel.scrollTo({ top: 0, behavior: 'smooth' });
}

export function resetWidgetForm(): void {
  state.editingWidgetId = null;
  state.inspectorVisualization = null;
  state.suppressInspectorEvents = true;
  controls.builderTitle.textContent = t(currentLocale(), 'selectWidget');
  setIconButton(controls.submitWidget, t(currentLocale(), 'addToDashboard'), 'plus', true);
  controls.title.value = '';
  controls.titleEn.value = '';
  controls.palette.value = DEFAULT_APPEARANCE.palette;
  controls.decimals.value = String(DEFAULT_APPEARANCE.decimals);
  controls.orientation.value = DEFAULT_APPEARANCE.orientation;
  controls.sortOrder.value = DEFAULT_APPEARANCE.sortOrder;
  controls.topN.value = String(DEFAULT_APPEARANCE.topN);
  controls.axisScale.value = DEFAULT_APPEARANCE.axisScale;
  controls.includeZero.value = DEFAULT_APPEARANCE.includeZero;
  controls.xAxisMin.value = '';
  controls.xAxisMax.value = '';
  controls.dataZoom.value = DEFAULT_APPEARANCE.dataZoom;
  controls.showPoints.value = DEFAULT_APPEARANCE.showPoints;
  controls.legendPosition.value = DEFAULT_APPEARANCE.legendPosition;
  controls.smooth.checked = DEFAULT_APPEARANCE.smoothLines;
  controls.patterns.checked = DEFAULT_APPEARANCE.usePatterns;
  controls.legend.checked = DEFAULT_APPEARANCE.showLegend;
  controls.labels.checked = DEFAULT_APPEARANCE.showLabels;
  controls.reverse.checked = DEFAULT_APPEARANCE.reversePalette;
  controls.classification.value = DEFAULT_APPEARANCE.classification;
  controls.classCount.value = String(DEFAULT_APPEARANCE.classCount);
  controls.manualBreaks.value = '';
  controls.nullColor.value = DEFAULT_APPEARANCE.nullColor;
  controls.mapScaleMode.value = DEFAULT_APPEARANCE.mapScaleMode;
  controls.mapBasemap.value = DEFAULT_APPEARANCE.mapBasemap;
  controls.mapFixedMin.value = '';
  controls.mapFixedMax.value = '';
  controls.timeControl.checked = DEFAULT_TIME.showControl;
  controls.conditionOperator.value = 'none';
  controls.conditionValue.value = '';
  controls.conditionColor.value = DEFAULT_APPEARANCE.condition.color;
  writeAppearanceControls(DEFAULT_APPEARANCE, '', DEFAULT_TIME);
  renderDimensions(state.dimensions, {});
  updateClassificationControls();
  updateConditionControls();
  updateVisualizationSpecificControls();
  updateAdvisor();
  clearFormError();
  state.suppressInspectorEvents = false;
  updateInspectorState();
  setBuilderOpen(false);
}

export async function submitWidget(event?: SubmitEvent, forceNew = false): Promise<void> {
  event?.preventDefault();
  clearFormError();
  try {
    syncLayoutsFromGrid();
    const widget = readWidgetFromForm(forceNew);
    await validateEmissWidgetPlan(widget);
    const index = state.definition.widgets.findIndex((item) => item.id === widget.id);
    if (index >= 0) {
      state.definition.widgets[index] = widget;
      replaceWidgetInGrid(widget);
      setStatus(`Виджет «${widget.title}» обновлён`, 'success');
      state.editingWidgetId = widget.id;
      updateInspectorState();
      focusWidgetOnCanvas(widget.id, false);
    } else {
      state.definition.widgets.push(widget);
      addWidgetToGrid(widget);
      void renderWidget(widget.id);
      setStatus(`Виджет «${widget.title}» добавлен`, 'success');
      state.editingWidgetId = widget.id;
      state.inspectorVisualization = widget.visualization;
      updateInspectorState();
      requestAnimationFrame(() => focusWidgetOnCanvas(widget.id));
    }
    updateWidgetCounter();
    scheduleGridMetricsUpdate();
    markDirty();
    if (narrowWorkspaceMedia.matches) {
      setPaletteOpen(false);
      setBuilderOpen(false);
    } else {
      setBuilderOpen(true);
    }
  } catch (error) {
    showFormError(errorMessage(error));
  }
}
